/**
 * The staff notice's reconciler, the one writer of `staffNotice` and
 * `noticeAct` (staff.state.ts): read under the staff member the session
 * names, recorded on their acknowledgment. A new member starts unanswered,
 * and an answer lands only while that member is still the one signed in
 * (switchMap drops it otherwise).
 */
import { session } from "@momo/views";
import { STAFF_NOTICE_VERSION } from "@revia/types";
import {
	catchError,
	distinctUntilChanged,
	EMPTY,
	exhaustMap,
	filter,
	from,
	map,
	merge,
	of,
	startWith,
	switchMap,
	tap,
} from "rxjs";
import { acknowledgeNotice, noticeAcknowledged } from "./staff.api.ts";
import {
	acknowledgeNotice$,
	noticeAct,
	noticeRetry$,
	type StaffNotice,
	staffMemberUid,
	staffNotice,
} from "./staff.state.ts";

session.$.pipe(
	map(() => staffMemberUid.get()),
	distinctUntilChanged(),
	tap(() => {
		staffNotice.set("unanswered");
		noticeAct.set("idle");
	}),
	switchMap((uid) =>
		uid === null
			? EMPTY
			: merge(
					noticeRetry$.pipe(
						startWith(undefined),
						tap(() => staffNotice.set("unanswered")),
						switchMap(() =>
							from(noticeAcknowledged(uid, STAFF_NOTICE_VERSION)).pipe(
								map((acknowledged): StaffNotice => (acknowledged ? "acknowledged" : "absent")),
								catchError((err: unknown) => {
									console.error("[staff] notice read failed", err);
									return of<StaffNotice>("error");
								}),
							),
						),
						tap((notice) => staffNotice.set(notice)),
					),
					acknowledgeNotice$.pipe(
						// Synchronous with the press: a second press while one is in
						// flight finds the act already busy.
						filter(() => noticeAct.value !== "busy"),
						tap(() => noticeAct.set("busy")),
						exhaustMap(() =>
							from(acknowledgeNotice(uid, STAFF_NOTICE_VERSION)).pipe(
								map(() => true),
								catchError((err: unknown) => {
									console.error("[staff] notice acknowledgment failed", err);
									return of(false);
								}),
							),
						),
						tap((recorded) => {
							if (recorded) staffNotice.set("acknowledged");
							noticeAct.set(recorded ? "idle" : "failed");
						}),
					),
				),
	),
).subscribe();
